/**
 * The student's user-step row from the ORM and what is read off its progress
 * (design §2): results, answered question ids, the skipped-filter flag.
 * Read-only — progress is written by the recorder.
 */
import { computed } from 'vue';
import { useRepo } from 'pinia-orm';
import { UserStep } from '../models/UserStep';
import type { UserStepProgress } from 'types/course/step';

const userStepRepo = useRepo(UserStep);

export interface UseUserStepRecordOptions {
  /** The user-step id (ORM primary key); invalid → no row. */
  userStepId: number;
}

export function useUserStepRecord({ userStepId }: UseUserStepRecordOptions) {
  const isSkippedStep = computed(() => {
    if (!repoUserStep.value || repoUserStep.value?.completion_date)
      return false;
    return !!currentParsedProgress.value?.isSkippedFilter;
  });

  const repoUserStep = computed(() => {
    if (!userStepId || isNaN(userStepId)) return null;
    return userStepRepo.withAllRecursive().find(userStepId);
  });

  const currentParsedProgress = computed<UserStepProgress | null>(() => {
    if (!repoUserStep.value || !repoUserStep.value?.progress) return null;
    return repoUserStep.value.progress;
  });

  const currentStepResults = computed(
    () =>
      (currentParsedProgress?.value?.stack?.results ??
        {}) as UserStepProgress['stack']['results'],
  );

  const answeredArray = computed(() => {
    if (!currentStepResults.value) return [];
    return Object.keys(currentStepResults.value).reduce<string[]>(
      (prev, curr) =>
        currentStepResults.value?.[curr]?.incomplete ? prev : [...prev, curr],
      [],
    );
  });

  // Explicit id only: stackIndex must not depend on currentStackItem (BUG-5).
  const isAnswered = (id: number | string | undefined): boolean => {
    if (id === undefined || id === null) return false;
    return (
      !!currentStepResults.value?.[`q_${id}`] &&
      !currentStepResults.value?.[`q_${id}`]?.incomplete
    );
  };

  return {
    repoUserStep,
    currentParsedProgress,
    currentStepResults,
    answeredArray,
    isAnswered,
    isSkippedStep,
  };
}

export type UseUserStepRecordReturn = ReturnType<typeof useUserStepRecord>;
